import { Link } from 'react-router-dom';
import { ArrowRight, Building2, ShieldCheck, ClipboardCheck, Users, BarChart3, Lock, Bell, FileText, CheckCircle2 } from 'lucide-react';
import Header from '../components/Header';

/**
 * /passport — Property Passport marketing page for professionals
 * Strata · Real Estate · Councils · Facilities · Developers · Insurance
 */
export default function PassportMarketing() {
  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="passport-marketing-page">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#E5E4E0] bg-[#1C1B1A] text-white">
        <div className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 border border-[#D96C4E] bg-[#D96C4E]/[0.1] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.28em] text-[#D96C4E]" data-testid="passport-hero-badge">
              <Lock size={12} /> Property Passport · Enterprise access
            </div>
            <h1 className="mt-6 font-heading text-5xl font-bold leading-[1.05] tracking-tight md:text-7xl" data-testid="passport-hero-title">
              Every property.
              <br />
              <span className="italic text-[#D96C4E]">Every job.</span>
              <br />
              One workspace.
            </h1>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-white/80" data-testid="passport-hero-subtitle">
              Property Passport is the professional maintenance platform behind Trades &amp; Neighbours — used by strata managers, real estate agencies, councils and facilities teams to track every job, contractor, compliance document and warranty across their entire property portfolio.
            </p>
            <div className="mt-10 flex flex-col gap-3 sm:flex-row">
              <Link to="/portfolio" className="btn-primary py-3" data-testid="passport-cta-portfolio">
                Open portfolio dashboard <ArrowRight size={16} />
              </Link>
              <Link to="/agency/pricing" className="inline-flex items-center justify-center gap-2 border border-white/40 px-5 py-3 text-sm font-semibold uppercase tracking-[0.15em] text-white transition-colors hover:bg-white/10" data-testid="passport-cta-pricing">
                See pricing <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Who uses it */}
      <section className="border-b border-[#E5E4E0] bg-white py-20">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <div className="mb-12 max-w-3xl">
            <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#D96C4E]">Who uses Property Passport</div>
            <h2 className="mt-3 font-heading text-4xl font-bold tracking-tight text-[#1C1B1A] md:text-5xl">Built for every property professional in Australia.</h2>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            {[
              { title: 'Strata Managers', body: '5 – 500 buildings' },
              { title: 'Real Estate Agencies', body: 'Sales + rentals' },
              { title: 'Facilities Managers', body: 'Commercial portfolios' },
              { title: 'Local Councils', body: 'Public asset maintenance' },
              { title: 'Developers', body: 'New estate handover' },
              { title: 'Insurance Assessors', body: 'Claims panel access' },
            ].map((r, i) => (
              <div key={i} className="border border-[#E5E4E0] p-5" data-testid={`passport-audience-${i}`}>
                <div className="font-heading text-lg font-bold text-[#1C1B1A]">{r.title}</div>
                <div className="mt-1 text-xs text-[#8A8988]">{r.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Animated Workflow Explainer */}
      <section className="border-b border-[#E5E4E0] bg-[#1C1B1A] py-24 text-white">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <div className="mb-14 max-w-3xl">
            <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#D96C4E]">How it works · Live workflow</div>
            <h2 className="mt-3 font-heading text-4xl font-bold tracking-tight md:text-5xl">
              One job. Ten steps.<br />
              <span className="italic text-[#D96C4E]">One record — forever.</span>
            </h2>
            <p className="mt-4 text-base text-white/70">
              Every job that flows through Trades &amp; Neighbours becomes part of the property's permanent Passport — automatically.
            </p>
          </div>

          {/* Animated flow chart */}
          <div className="relative">
            {/* Connecting rail */}
            <div className="pointer-events-none absolute left-6 top-2 hidden h-[calc(100%-16px)] w-px bg-[#D96C4E]/30 md:left-1/2 md:block md:-translate-x-1/2" />
            <ol className="space-y-6">
              {[
                { step: 'Property', body: 'Address is claimed by owner or added by strata/agent.', side: 'left' },
                { step: 'Job created', body: 'Homeowner or manager posts a job with photos and scope.', side: 'right' },
                { step: 'Tradies quote', body: 'Verified tradies submit quotes. Client sees them side-by-side.', side: 'left' },
                { step: 'Quote approved', body: 'One tap accepts a quote. Job is locked in.', side: 'right' },
                { step: 'Purchase order', body: 'Auto-generated PO with scope, terms, and dates.', side: 'left' },
                { step: 'Messages', body: 'Chat trail stored with the job. No lost emails.', side: 'right' },
                { step: 'Photos & docs', body: 'Before/after photos and permits uploaded to the vault.', side: 'left' },
                { step: 'Invoice', body: 'Tradie invoices from the job. Client pays direct.', side: 'right' },
                { step: 'Job complete', body: 'Warranty details captured. Timeline entry created.', side: 'left' },
                { step: 'Passport updated', body: 'Property record auto-refreshes. Everyone sees the same source of truth.', side: 'right', accent: true },
              ].map((s, i) => (
                <li
                  key={i}
                  className={`tn-flow-row ${s.side === 'left' ? 'md:justify-start' : 'md:justify-end'} flex items-start gap-4 md:gap-8`}
                  style={{ animationDelay: `${i * 90}ms` }}
                  data-testid={`flow-step-${i}`}
                >
                  {s.side === 'left' && <div className="hidden flex-1 md:block" />}
                  <div className={`relative flex-1 border ${s.accent ? 'border-[#D96C4E] bg-[#D96C4E]/[0.08]' : 'border-white/15 bg-white/[0.03]'} p-5`}>
                    <div className="absolute -left-3 top-4 hidden h-6 w-6 items-center justify-center rounded-full bg-[#D96C4E] text-[10px] font-bold text-white md:flex md:left-auto md:right-auto md:top-6" style={{
                      left: s.side === 'left' ? undefined : '-12px',
                      right: s.side === 'left' ? '-12px' : undefined,
                    }}>{i + 1}</div>
                    <div className={`text-[10px] font-semibold uppercase tracking-[0.24em] ${s.accent ? 'text-[#D96C4E]' : 'text-white/60'}`}>Step {i + 1}</div>
                    <div className="mt-1 font-heading text-lg font-bold text-white">{s.step}</div>
                    <div className="mt-1 text-sm text-white/70">{s.body}</div>
                  </div>
                  {s.side === 'right' && <div className="hidden flex-1 md:block" />}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* What you see */}
      <section className="border-b border-[#E5E4E0] bg-[#FAF9F6] py-20">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <div className="mb-14 max-w-3xl">
            <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#1A362D]">What you get inside</div>
            <h2 className="mt-3 font-heading text-4xl font-bold tracking-tight text-[#1C1B1A] md:text-5xl">
              A portfolio dashboard that finally makes sense.
            </h2>
            <p className="mt-4 text-base text-[#5C5B5A]">Every property you manage becomes a living record. Click any address and see everything — jobs, quotes, purchase orders, messages, photos, invoices, warranties, compliance documents, and a full audit trail.</p>
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: Building2, title: 'Portfolio view', body: 'Every building. Every unit. Every job status. At a glance.' },
              { icon: FileText, title: 'Maintenance timeline', body: 'A permanent, chronological record of every job done at every property.' },
              { icon: ShieldCheck, title: 'Compliance dashboard', body: 'Track every contractor\'s licence, insurance and certification. Alerts before anything expires.' },
              { icon: ClipboardCheck, title: 'Full audit trail', body: 'Every quote · PO · message · photo · invoice attached to the job. Ready for AGM or tender packs.' },
              { icon: Users, title: 'Contractor panel', body: 'Approved tradies for your portfolio. Emergency-response SLA panel. Bulk work orders.' },
              { icon: BarChart3, title: 'Portfolio reports', body: 'Spend by suburb. Category breakdowns. Contractor performance. Exportable to PDF/CSV.' },
              { icon: Bell, title: 'Expiry alerts', body: 'SMS + email before a contractor\'s insurance lapses on a live job. Legal liability, gone.' },
              { icon: Lock, title: 'Private &amp; secure', body: 'Role-based access. Data encrypted at rest. Australian Privacy Act 1988 compliant.' },
              { icon: CheckCircle2, title: 'Warranty vault', body: 'All warranty docs stored per-property. Buyer-ready. Never lost.' },
            ].map((f, i) => (
              <div key={i} className="border border-[#E5E4E0] bg-white p-6" data-testid={`passport-feature-${i}`}>
                <div className="flex h-10 w-10 items-center justify-center bg-[#1A362D] text-white">
                  <f.icon size={18} />
                </div>
                <h3 className="mt-4 font-heading text-lg font-bold text-[#1C1B1A]">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#5C5B5A]">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why now */}
      <section className="border-b border-[#E5E4E0] bg-white py-20">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-2">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#D96C4E]">Why property professionals switch</div>
              <h2 className="mt-3 font-heading text-4xl font-bold tracking-tight text-[#1C1B1A]">Because paperwork is losing you money.</h2>
              <p className="mt-5 text-base text-[#5C5B5A]">
                One expired insurance policy on a live job. One warranty document lost in email. One unlicensed contractor on a strata site. Any of these can cost tens of thousands — or trigger legal liability.
              </p>
              <p className="mt-4 text-base text-[#5C5B5A]">
                Property Passport gives you one shared record connected to Australia's growing verified tradie network. Every job you commission becomes part of the property's permanent history — searchable, exportable, buyer-ready.
              </p>
            </div>
            <div className="border border-[#1C1B1A] bg-[#FAF9F6] p-8">
              <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#1A362D]">Included in every tier</div>
              <ul className="mt-4 space-y-3 text-sm text-[#1C1B1A]">
                {[
                  'Unlimited properties in your portfolio',
                  'Access to Australia-wide verified tradie network',
                  'Full compliance vault per contractor',
                  'Job workflow: quote → PO → invoice → complete',
                  'Portfolio-wide reports (PDF, CSV export)',
                  'Contractor panel management',
                  'Access to peer conduct signals (Risk &amp; Trust)',
                  'Privacy Act 1988 compliant data handling',
                  'Australian-based support',
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-2" data-testid={`included-${i}`}>
                    <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0 text-[#1A362D]" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link to="/agency/pricing" className="btn-primary mt-8 inline-flex w-full justify-center" data-testid="passport-tiers-btn">
                See all tiers <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1A362D] py-20 text-white">
        <div className="mx-auto max-w-4xl px-6 text-center md:px-10">
          <h2 className="font-heading text-4xl font-bold leading-tight md:text-5xl">Ready to see your portfolio in one place?</h2>
          <p className="mt-5 text-lg text-white/80">Open the portfolio dashboard and add your first property — it takes 60 seconds.</p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Link to="/portfolio" className="btn-primary bg-[#D96C4E] hover:bg-[#c95d3e]" data-testid="passport-final-cta">
              Open Portfolio Dashboard <ArrowRight size={16} />
            </Link>
            <Link to="/register" className="inline-flex items-center justify-center gap-2 border border-white px-5 py-3 text-sm font-semibold uppercase tracking-[0.15em] text-white hover:bg-white/10" data-testid="passport-register-cta">
              Create an account
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
